import React, { useState } from "react";
import useEditBlog from "../hooks/useEditBlog";
import useUploadFiles from "../../../services/hooks/useUploadFiles";

// components
import RichText from "../../../component/element/forms/RichText";
import TextInput from "../../../component/element/forms/TextInput";
import SelectOption from "../../../component/element/forms/SelectOption";
import Upload from "../../../component/modals/upload";
import Button from "../../../component/element/forms/Button";

// constants
import { MdSubtitles } from "react-icons/md";
import { FaArrowLeftLong } from "react-icons/fa6";

// state
import {
  get_categories,
  edit_blog,
  get_blog,
} from "../../../services/apis/blog";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useNavigate, useParams } from "react-router-dom";

const EditBlog: React.FC = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [imgPath, setImgPath] = useState("");
  const [notification, setNotification] = useState(false);
  const categories = useAppSelector(({ blog }) => blog.categories);
  const loading = useAppSelector(({ blog }) => blog.loading);

  const { uploadFile, deleteFiles, loader } = useUploadFiles();

  const { data, error, setData, setError, onValidate } = useEditBlog();

  const handleOk = () => {
    const validate = onValidate();
    if (!validate) return;
    try {
      edit_blog(data, dispatch);
      setNotification(true);
    } catch (err) {
      console.log(err);
      setNotification(false);
    }
  };

  const onLoad = React.useCallback(() => {
    if (id) {
      get_blog(id, dispatch);
      get_categories(dispatch);
    }
  }, [dispatch, id]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  return (
    <div className="w-full ">
      <div className="w-full flex justify-between items-center mb-6">
        <h2 className="font-bold text-xl capitalize mb-0">Edit Blog</h2>

        <Button
          title="View Type"
          children={<FaArrowLeftLong className="mr-2" />}
          onClick={() => navigate(-1)}
          className="px-4"
        />
      </div>

      {notification && (
        <div
          className="mb-4 p-4 text-sm text-green-700 bg-green-100 rounded-lg dark:bg-green-200 dark:text-green-800"
          role="alert"
        >
          <span className="font-medium">Success!</span> Blog updated
          successfully.
        </div>
      )}

      <div className="w-full mb-6 bg-white rounded-xl shadow-md p-6">
        <Upload
          title="Upload Image"
          label="JPG or PNG"
          name="blog_file"
          loading={loader}
          status={error.image}
          completed={!!data.image}
          imgPreview={data.image}
          onChange={async (fileOrFiles) => {
            const paths = await uploadFile(fileOrFiles as File, "blog");
            if (paths.length > 0) {
              const img_url = paths[0].base_url + "/" + paths[0].path;
              setData((prev) => ({ ...prev, image: img_url }));
              setError((prev) => ({ ...prev, image: "" }));
              setImgPath(img_url);
            }
          }}
          onDelete={async () => {
            if (imgPath) {
              await deleteFiles(imgPath);
              setData((prev) => ({ ...prev, image: "" }));
            }
          }}
          className="w-full mb-6"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="">
            <TextInput
              name="title"
              type="text"
              label="Title"
              value={data.title}
              placeholder="Enter Title"
              icon={<MdSubtitles size={20} />}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value, name } = e.target;
                setData((prev) => ({ ...prev, [name]: value }));
                setError((prev) => ({ ...prev, [name]: "" }));
              }}
            />
          </div>

          <div className="">
            <SelectOption
              items={categories}
              label="Category"
              placeholder="Select Category"
              value={data.blog_category_id?.toString()}
              onChange={(res) => {
                setData((prev) => ({
                  ...prev,
                  blog_category_id: Number(res),
                }));
                setError((prev) => ({ ...prev, blog_category_id: "" }));
              }}
            />
          </div>
        </div>

        <div className="w-full my-4">
          <RichText
            value={data.article}
            placeholder="Write Article"
            onChange={(res) => setData((prev) => ({ ...prev, article: res }))}
          />
        </div>

        <div className="flex justify-end items-center">
          <div className="">
            <Button
              type="submit"
              title="Submit"
              loading={!loading}
              onClick={handleOk}
              className="px-4"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBlog;
